/**
 * Safe numeric expression evaluator for custom analytics.
 * Allowed: numbers, + - * / ( ), identifiers (must exist in variables map).
 * No functions, no eval.
 */

function tokenize(str) {
  const s = String(str || "").trim();
  const tokens = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < s.length && /[0-9.]/.test(s[j])) j++;
      const v = parseFloat(s.slice(i, j));
      if (Number.isNaN(v)) throw new Error("Invalid number");
      tokens.push({ type: "num", v });
      i = j;
      continue;
    }
    if (/[a-zA-Z_]/.test(c)) {
      let j = i;
      while (j < s.length && /[a-zA-Z0-9_]/.test(s[j])) j++;
      tokens.push({ type: "id", v: s.slice(i, j).toLowerCase() });
      i = j;
      continue;
    }
    if ("+-*/()".includes(c)) {
      tokens.push({ type: "op", v: c });
      i++;
      continue;
    }
    throw new Error(`Invalid character: ${c}`);
  }
  return tokens;
}

class Parser {
  constructor(tokens, variables) {
    this.t = tokens;
    this.i = 0;
    this.vars = variables;
  }
  peek() {
    return this.t[this.i];
  }
  eat(type, v) {
    const tok = this.peek();
    if (!tok || tok.type !== type || (v != null && tok.v !== v)) {
      throw new Error("Unexpected token in formula");
    }
    this.i++;
    return tok;
  }
  parseExpr() {
    let n = this.parseTerm();
    while (this.peek()?.type === "op" && (this.peek().v === "+" || this.peek().v === "-")) {
      const op = this.eat("op").v;
      const r = this.parseTerm();
      n = op === "+" ? n + r : n - r;
    }
    return n;
  }
  parseTerm() {
    let n = this.parseFactor();
    while (this.peek()?.type === "op" && (this.peek().v === "*" || this.peek().v === "/")) {
      const op = this.eat("op").v;
      const r = this.parseFactor();
      if (op === "/") {
        if (r === 0) n = 0;
        else n = n / r;
      } else {
        n = n * r;
      }
    }
    return n;
  }
  parseFactor() {
    const tok = this.peek();
    if (!tok) throw new Error("Incomplete formula");
    if (tok.type === "num") {
      this.eat("num");
      return tok.v;
    }
    if (tok.type === "id") {
      this.eat("id");
      const key = tok.v;
      if (!(key in this.vars)) throw new Error(`Unknown variable: ${key}`);
      const val = Number(this.vars[key]);
      return Number.isFinite(val) ? val : 0;
    }
    if (tok.type === "op" && tok.v === "(") {
      this.eat("op", "(");
      const inner = this.parseExpr();
      this.eat("op", ")");
      return inner;
    }
    throw new Error("Invalid formula");
  }
}

/**
 * @param {string} expression
 * @param {Record<string, number>} variables — keys lowercased at lookup
 */
export function evaluateFormula(expression, variables) {
  const vars = {};
  for (const [k, v] of Object.entries(variables || {})) {
    vars[String(k).toLowerCase()] = typeof v === "number" && Number.isFinite(v) ? v : 0;
  }
  const tokens = tokenize(expression);
  if (tokens.length === 0) throw new Error("Empty formula");
  const p = new Parser(tokens, vars);
  const out = p.parseExpr();
  if (p.i !== tokens.length) throw new Error("Trailing characters in formula");
  return out;
}
